/**
 * FEAT-ATTRIB-003 `C2` — the `trait` segment condition, client side.
 *
 * `C2` landed the condition type in BOTH cohort engines
 * (`Right-Route-Server` and `xnotify-cohortbe`, byte-identical, 12 cases
 * each). `DA-BUG-ATTR-0244` then found that the builder offers six condition
 * types and `trait` is not one of them, so the engine could resolve a segment
 * the product could not express. This is the half that closes that.
 *
 * ## Why the logic lives here and not in the JSX
 *
 * `react-scripts`'s Jest resolver does not honour Chakra's `exports` map, so
 * no Chakra component in this app renders under test — a pre-existing
 * limitation `A2` met the same way (`impactView.js`). Rather than claim
 * coverage the runner cannot give, the part that can be wrong lives in a pure
 * module and the JSX only wires it.
 *
 * ## The property that matters, and it fails SILENTLY
 *
 * The engine's `buildTraitConditionFilter` returns **null** for a comparison
 * operator whose value is not a finite number:
 *
 *     case "greater_than":  return numOk ? { [path]: { $gt: num } } : null;
 *
 * A null filter is a condition that does not constrain, so a segment built
 * with `credited_conversions greater_than "five"` does not error — it matches
 * a WIDER population than the author asked for. Nothing on screen says so.
 * That is why `numericOnly` is enforced here, before the payload is built,
 * rather than left to a server round-trip.
 *
 * `exists` / `not_exists` take no value at all, because they assert on
 * presence; sending one would be meaningless and the engine ignores it.
 */

/**
 * Mirrors `utils/cohortFiltersShared.js#isSafeTraitPath` in
 * `Right-Route-Server` and `xnotify-cohortbe`.
 *
 * `trait_key` becomes a Mongo FIELD PATH, so an unvalidated key is a
 * query-operator injection: `$where` in key position changes what the query
 * MEANS, not what it matches. The server validates it and must stay the
 * authority — this copy exists so the builder refuses to assemble a condition
 * the server will reject, not to replace that check.
 *
 * The rule, stated so a reader can compare it to the server without running
 * either: at most 200 characters; no `$` and no NUL anywhere; and
 * dot-separated runs of letters, digits and underscores, which rejects a
 * leading dot, a trailing dot and a double dot.
 */
export const TRAIT_PATH_MAX = 200;
const TRAIT_PATH_RE = /^[A-Za-z0-9_]+(\.[A-Za-z0-9_]+)*$/;

export function isSafeTraitPath(key) {
  if (!key || key.length > TRAIT_PATH_MAX) return false;
  if (key.includes('$') || key.includes('\0')) return false;
  return TRAIT_PATH_RE.test(key);
}

/**
 * The engine's eight operators, each carrying what it needs.
 *
 * `numericOnly` is the flag that prevents the silent widening above. `equals`
 * and `not_equals` are deliberately NOT numeric-only: the engine compares
 * numerically when the value parses and exactly otherwise, because a trait
 * may be a label rather than a count.
 */
export const TRAIT_OPERATORS = [
  { value: 'greater_than',          label: 'Greater Than',          needsValue: true,  numericOnly: true },
  { value: 'greater_than_or_equal', label: 'Greater Than or Equal', needsValue: true,  numericOnly: true },
  { value: 'less_than',             label: 'Less Than',             needsValue: true,  numericOnly: true },
  { value: 'less_than_or_equal',    label: 'Less Than or Equal',    needsValue: true,  numericOnly: true },
  { value: 'equals',                label: 'Equals',                needsValue: true,  numericOnly: false },
  { value: 'not_equals',            label: 'Not Equals',            needsValue: true,  numericOnly: false },
  { value: 'exists',                label: 'Is Set',                needsValue: false, numericOnly: false },
  { value: 'not_exists',            label: 'Is Not Set',            needsValue: false, numericOnly: false },
];

export function traitOperator(op) {
  return TRAIT_OPERATORS.find((o) => o.value === String(op || '').toLowerCase()) || null;
}

/**
 * `greater_than` rather than `equals`, unlike every other condition type.
 *
 * §7.2 chose this activation path over a label because it yields a NUMERIC
 * THRESHOLD, and a threshold is what an attribution number is for. Defaulting
 * to `equals` would make the common case ("credited more than N") two clicks
 * away and the rare case the default.
 */
export function traitConditionDefaults(base) {
  return { ...base, trait_key: '', operator: 'greater_than', value: '' };
}

/** Type-safe presence, per ANTI-17 / DA-BUG-0214: `value` may be a number. */
function hasValue(v) {
  if (v === undefined || v === null) return false;
  if (typeof v === 'number') return Number.isFinite(v);
  return String(v).trim() !== '';
}

/**
 * Why a condition cannot be saved yet, or null when it can.
 *
 * Returns a REASON rather than a boolean so the form can say which of the
 * three problems it is. A bare `false` on an unsafe key and on a missing
 * threshold reads identically to the author, and they are fixed differently.
 */
export const TRAIT_INCOMPLETE = Object.freeze({
  NO_KEY: 'Choose a trait',
  UNSAFE_KEY: 'A trait name may use only letters, digits, underscores and dots',
  NO_OPERATOR: 'Choose an operator',
  NO_VALUE: 'Enter a value',
  NOT_A_NUMBER: 'This operator compares numbers, so the value must be a number',
});

export function traitConditionProblem(c) {
  const key = String(c && c.trait_key != null ? c.trait_key : '').trim();
  if (key === '') return TRAIT_INCOMPLETE.NO_KEY;
  if (!isSafeTraitPath(key)) return TRAIT_INCOMPLETE.UNSAFE_KEY;

  const op = traitOperator(c && c.operator);
  if (!op) return TRAIT_INCOMPLETE.NO_OPERATOR;
  if (!op.needsValue) return null;

  if (!hasValue(c.value)) return TRAIT_INCOMPLETE.NO_VALUE;
  /**
   * The silent-widening guard. `Number('')` is 0, so presence is checked
   * FIRST — the same ordering the engine uses, and for the same reason.
   */
  if (op.numericOnly && !Number.isFinite(Number(c.value))) {
    return TRAIT_INCOMPLETE.NOT_A_NUMBER;
  }
  return null;
}

export function isTraitConditionComplete(c) {
  return traitConditionProblem(c) === null;
}

/**
 * The wire shape, matching `cohortConditionValidate.js`'s `case "trait"`.
 *
 * `value` is OMITTED for the two presence operators rather than sent empty:
 * the engine ignores it, and sending a field the condition does not have
 * invites a later reader to believe it means something.
 */
export function buildTraitConditionPayload(c) {
  const op = traitOperator(c && c.operator);
  const payload = {
    type: 'trait',
    trait_key: String(c && c.trait_key != null ? c.trait_key : '').trim(),
    operator: op ? op.value : '',
  };
  if (op && op.needsValue) payload.value = c.value;
  return payload;
}

/** The one-line summary the collapsed condition row shows. */
export function traitConditionSummary(c) {
  const key = String(c && c.trait_key != null ? c.trait_key : '').trim() || 'Trait';
  const op = traitOperator(c && c.operator);
  if (!op) return `${key} …`;
  if (!op.needsValue) return `${key} ${op.label.toLowerCase()}`;
  const shown = hasValue(c && c.value) ? String(c.value) : '""';
  return `${key} ${op.label.toLowerCase()} ${shown}`;
}
